import { selector, selectorFamily } from "recoil";

import { SelectionEnum } from "../../enums";
import {
  collectCoursesCalendarSlotIds,
  collectCoursesCalendarSlotIdsByDate,
  collectCoursesCalendarSlotIdsByHour,
  getCoursesCalendarSelectionState,
  normalizeCoursesCalendarSelectedSlotIds,
} from "../../helpers/courses-calendar";
import { DateYMDString, ICoursesCalendarSlotDto } from "../../models";
import {
  coursesCalendarSlotsAtom,
  selectedCoursesCalendarSlotIdsAtom,
} from "../atoms/courses-calendar.atoms";

export const allCoursesCalendarSlotIdsSelector = selector<Set<string>>({
  key: "allCoursesCalendarSlotIdsSelector",
  get: ({ get }) =>
    collectCoursesCalendarSlotIds(get(coursesCalendarSlotsAtom)),
});

export const coursesCalendarSlotIdsByHourSF = selectorFamily<
  Set<string>,
  number
>({
  key: "coursesCalendarSlotIdsByHourSF",
  get:
    hourIndex =>
    ({ get }) =>
      collectCoursesCalendarSlotIdsByHour({
        slots: get(coursesCalendarSlotsAtom),
        hourIndex,
      }),
});

export const coursesCalendarSlotIdsByDateSF = selectorFamily<
  Set<string>,
  DateYMDString
>({
  key: "coursesCalendarSlotIdsByDateSF",
  get:
    dateKey =>
    ({ get }) =>
      collectCoursesCalendarSlotIdsByDate({
        slots: get(coursesCalendarSlotsAtom),
        dateKey,
      }),
});

export const allRecordsSelectedCoursesCalendarSelector =
  selector<SelectionEnum>({
    key: "allRecordsSelectedCoursesCalendarSelector",
    get: ({ get }) => {
      const selected = normalizeCoursesCalendarSelectedSlotIds(
        get(selectedCoursesCalendarSlotIdsAtom),
      );
      const allSlotIds = get(allCoursesCalendarSlotIdsSelector);
      return getCoursesCalendarSelectionState(selected, allSlotIds);
    },
  });

export const wholeRowSelectedCoursesCalendarSF = selectorFamily<
  SelectionEnum,
  number
>({
  key: "wholeRowSelectedCoursesCalendarSF",
  get:
    hourIndex =>
    ({ get }) => {
      const selected = normalizeCoursesCalendarSelectedSlotIds(
        get(selectedCoursesCalendarSlotIdsAtom),
      );
      const rowSlotIds = get(coursesCalendarSlotIdsByHourSF(hourIndex));
      return getCoursesCalendarSelectionState(selected, rowSlotIds);
    },
});

export const wholeColumnSelectedCoursesCalendarSF = selectorFamily<
  SelectionEnum,
  DateYMDString
>({
  key: "wholeColumnSelectedCoursesCalendarSF",
  get:
    date =>
    ({ get }) => {
      const selected = normalizeCoursesCalendarSelectedSlotIds(
        get(selectedCoursesCalendarSlotIdsAtom),
      );
      const colSlotIds = get(coursesCalendarSlotIdsByDateSF(date));
      return getCoursesCalendarSelectionState(selected, colSlotIds);
    },
});

export const coursesCalendarSelectedSlotsCountSelector = selector<number>({
  key: "coursesCalendarSelectedSlotsCountSelector",
  get: ({ get }) =>
    normalizeCoursesCalendarSelectedSlotIds(
      get(selectedCoursesCalendarSlotIdsAtom),
    ).size,
});

export const selectedCoursesCalendarSlotsSelector = selector<
  ICoursesCalendarSlotDto[]
>({
  key: "selectedCoursesCalendarSlotsSelector",
  get: ({ get }) => {
    const selected = normalizeCoursesCalendarSelectedSlotIds(
      get(selectedCoursesCalendarSlotIdsAtom),
    );
    const slots = get(coursesCalendarSlotsAtom);
    if (!selected.size) {
      return [];
    }
    return slots.filter(slot => selected.has(slot.id));
  },
});
